<template>
    <div class="participant-score-chart">
        <highcharts :options="chartOptions"></highcharts>
    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>

import Vue from 'vue'
import * as _ from 'lodash'
import moment from 'moment'
import Colors from '@/providers/GaugeColors'
import { store } from '@/apps/default/store'

/**
 * Component to display WBI questionnaire scores over time
 */
export default Vue.extend({
    name: 'ParticipantScoreChart',
    props: {
        scores: String,
        showYAxis: {
            default: false,
            type: Boolean
        },
        yValues: {
            default: null,
            type: Array
        }
    },
    data () {
        return {}
    },
    computed: {
        participantScores () {
            return this.$store.state.patientParticipant.latestScoreDefs
        },
        scoreHistory () {
            return this.$store.state.participantScores
        },
        scoreSeriesDef () {
            return this.scoreHistory[this.scores]
        },

        chartOptions () {
            const options = this.getBaseOptions()
            if (this.scoreSeriesDef) {
                this.addScoreOptions(options)
            }
            return options
        },
        chartTitle () {
            return this.$i18n.gt(`HEADING_${this.scores}`)
        },
        reverseYAxis () {
            // if (this.scoreSeriesDef) {
            //     return this.scoreSeriesDef.reverse_trend_y_axis
            // } else {
            return false
            // }
        }

    },
    methods: {
        getBaseOptions () {
            return {
                chart: {
                    borderColor: '#fff',
                    type: 'line',
                    height: 180,
                    marginTop: 30,
                    backgroundColor: 'transparent',
                    showAxes: false,
                },
                title: {
                    text: this.chartTitle,
                    align: 'left',
                    floating: true,
                    style: {
                        fontFamily: 'helvetica, arial, sans-serif',
                        fontSize: '14px'
                    }
                },
                credits: { enabled: false },
                tooltip: { enabled: true },
                legend: { enabled: false },

                plotOptions: {
                    series: {
                        color: `rgb(128, 128, 128, 1)`,
                        marker: {
                            symbol: 'circle',
                            radius: 0,
                        },
                        tooltip: {
                            pointFormatter: function () {
                                return `${this.y}`
                            }
                        },
                    },
                    columnrange: {
                        grouping: false
                    }
                },

                xAxis: {
                    visible: true,
                    type: 'datetime',
                    tickLength: 0,
                    title: null,
                    lineWidth: 0,
                    labels: {
                        enabled: false
                    },
                },

                yAxis: [{
                    gridLineWidth: 0,
                    visible: true,
                    title: null,
                    reversed: this.reverseYAxis,
                    endOnTick: false,
                    startOnTick: false,
                    tickPositions: this.yValues,
                    labels: {
                        enabled: this.showYAxis
                    },
                }],

                series: [
                    // main data series
                    {
                        type: 'line',
                        data: [],
                        zones: [{}, {}, {}, {}]
                    },
                    // series for threshold indicators (displayed as a stacked column)
                    {
                        type: 'columnrange',
                        borderWidth: 10,
                        tooltip: {},
                    },
                    { type: 'columnrange', borderWidth: 10 },
                    { type: 'columnrange', borderWidth: 10 },
                    { type: 'columnrange', borderWidth: 10 },
                ]
            }
        },
        addScoreOptions (options) {
            let minY
            let maxY
            let xCount

            // get main series data - pad or trim to exactly 10 entries,
            // to make the threshold indicator bars come out at a consistent width
            const DATA_POINTS = 10
            if (this.scoreHistory[this.scores]) {
                let scores = _.take(this.scoreHistory[this.scores].scores_or_values, DATA_POINTS)
                if (scores.length === 1) {
                    options.series[0].marker = {
                        symbol: 'circle',
                        radius: 3,
                        states: {
                            hover: {
                                radiusPlus: 0,
                                lineWidthPlus: 0,
                            }
                        }
                    }
                }

                while (scores.length < 10) {
                    scores.push({ timestamp: null, score_or_value: null })
                }

                options.series[0].data = _.map(
                    _.reverse(scores), // scores are latest-first, so flip order
                    (entry) => {
                        return [
                            moment(entry.timestamp).format('M/D/YYYY'),
                            entry.score_or_value ? Math.floor(entry.score_or_value) : null
                        ]
                    })

                xCount = options.series[0].data.length
                minY = +_.minBy(options.series[0].data, (point) => {
                    return point[1]
                })[1]
                maxY = +_.maxBy(options.series[0].data, (point) => {
                    return point[1]
                })[1]
            }

            // calculate threshold indicator series based on different options
            const limits = this.scoreSeriesDef.limits
            const thresholds = this.scoreSeriesDef.thresholds
            const higherIsBetter = this.scoreSeriesDef.higher_is_better
            const useCustomColors = this.scoreSeriesDef.use_custom_colors


            const plotBands = []
            const bandColors = !useCustomColors ? Colors.GAUGE_PLOT_BAND_COLORS : Colors.CUSTOM_GAUGE_PLOT_BAND_COLORS[store.state.theme.name]
            if (thresholds) {
                for (var i = 0; i <= thresholds.length; i++) {
                    let low = null
                    let high = null
                    let color = null

                    if (i == 0) {
                        if (minY < +limits[0]) {
                            low = minY - Math.abs((+limits[1] - +limits[0]) / 10)
                        } else {
                            low = +limits[0] - Math.abs((+limits[1] - +limits[0]) / 10)
                        }
                        options.yAxis[0].min = low
                        high = +thresholds[0]
                        color = (higherIsBetter ? bandColors[thresholds.length] : bandColors[0])
                    } else if (i == thresholds.length) {
                        low = +thresholds[i - 1]
                        if (maxY > +limits[1]) {
                            high = maxY + Math.abs((+limits[1] - +limits[0]) / 10)

                        } else high = +limits[1] + Math.abs((+limits[1] - +limits[0]) / 10)
                        options.yAxis[0].max = high
                        color = (higherIsBetter ? bandColors[0] : bandColors[thresholds.length])
                    } else {
                        low = +thresholds[i - 1]
                        high = +thresholds[i]
                        color = (higherIsBetter ? bandColors[thresholds.length - i] : bandColors[i])
                    }

                    plotBands.push({ from: low, to: high, color: color })
                }
                options.yAxis[0].plotBands = plotBands
            }
        }
    },
})

</script>
